<template>
  <div id="puzzle-solution">
    <vue-markdown>
    Trying to draw a few things in the bottom left canvas reveals the main mechanics of the game: 
    
    1. On each level, you're asked to draw a particular shape (circle, triangle, or square), indicated by the blue text and blue outline on the canvas. 

    1. On each level, the border only permits a particular shape (circle, triangle, or square) through, indicated by the blue shape in the border, and this is not necessarily the same shape as the one you have to draw.

    1. When you finish drawing, the grey path either fades away or turns blue. If your drawing resembles the requested shape, it turns blue and moves to the border shape detector. 

    1. If the border detected shape matches the permitted shape, your shape is let through to the right and you advance to the next level. If the border denies the shape entry, you lose a level.

    However, the border shape detector seems to classify shapes in very strange ways. Using trial and error, you can deduce the information encoded in your drawing that classifies it as a circle, triangle, or square according to the border's "basic heuristics" (similar to a game of Mastermind).

    1. If the shape is drawn with more than one stroke, the border detects a **square**.

    1. If the shape is drawn clockwise, the border detects a **triangle**.

    1. If the shape is drawn counter-clockwise, the border detects a **circle**.

    With this information, you can always "disguise" a drawn shape so that the border will allow your shape through, regardless of its appearance. Completing all 10 levels will show all your drawn shapes at home, and reveals the path they took to get there:
    </vue-markdown>
    <img src="@/assets/img/soln-draw.png">
    <vue-markdown>
    This is the trident logo used for Universal Serial Bus (USB). The text clues the first of three words, so the answer is **UNIVERSAL**.

    ---

    *This puzzle was inspired by:*
    1. *The game [Papers, Please](https://papersplea.se/), where you play as an immigration officer at a strict border, and*

    2. *Google [Quick, Draw!](https://quickdraw.withgoogle.com/), and specifically this [article](https://qz.com/994486/the-way-you-draw-circles-says-a-lot-about-you/), which used the dataset to investigate how different cultures might draw universal shapes differently.*
    
    *Most interestingly, the data found that people from across the world tend to draw circles counter-clockwise, with the exception of Taiwan and Japan. This led to the creation of this puzzle, which forces you to think about how you secretly encode information (i.e. direction) in drawing simple shapes without even knowing it.*

    *The more accurate shape detector (which fades your drawing if it doesn't detect the right shape) is a basic neural network (using Neataptic) trained on circles, squares, triangles, and "squiggles" from the Google Quick, Draw! dataset. Since the architecture was really simple and the training set small, it sometimes misdetects your drawing, even when it looks quite like the intended shape. Sorry about that!*

    *The border shape detector uses a [curve orientation](https://en.wikipedia.org/wiki/Curve_orientation) algorithm modified from the above Quartz article to detect whether your shape was drawn clockwise or counter-clockwise.*

    *During the live hunt, getting a shape denied at the border didn't penalize you by losing a level of progress. This meant that many teams were able to brute force drawings to reach the end of the game without figuring out the border rules, which is why I added the [Witness](https://en.wikipedia.org/wiki/The_Witness_\(2016_video_game\))-style penalty. The final answer was only clued by a blue line (without the question mark), and many teams were stuck - apologies for this.*


    </vue-markdown>
  </div>
</template>

<script>
import VueMarkdown from 'vue-markdown-v2'

export default {
  components: {
    VueMarkdown
  }  
}
</script>